<template>
  <!-- Main Modal -->
  <modal
    elem-id="modal_bloqueios_agenda"
    size="compactDesktop"
    :title="t('Bloqueio de agenda')"
    :save-label="t('Salvar')"
    :close-label="t('Fechar')"
    :show="isVisible"
    :save-disabled-fn="() => !canSave"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose">
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
    <div class="delete-button-container" v-if="bloqueioData.bloqueio_agenda_id > 0">
      <Button
        data-testid="agenda-bloqueio-deletar-btn"
        :label="t('Deletar bloqueio')"
        icon="pi pi-times"
        severity="danger"
        @click="showDeleteConfirmation"
      />
    </div>

    <box
      type="primary"
      :validation="() => validationErrors"
      :title="t('Informações gerais')"
      elem-id="boxInformacoesGeraisBloqueioAgenda">

      <input-text
        data-testid="agenda-bloqueio-titulo-input"
        :label="t('Título')"
        :required="true"
        :validation="() => isTituloValid"
        v-model="bloqueioData.bloqueio_agenda_titulo"
        elem-id="bloqueio_agenda_titulo">
      </input-text>

      <select-list
        data-testid="agenda-bloqueio-operador-select"
        :label="t('Operador')"
        :required="true"
        :validation="() => isOperadorValid"
        :itens-select="operadoresOptions"
        v-model="bloqueioData.bloqueio_agenda_operador_user_id"
        elem-id="selectOperadorBloqueioAgenda">
      </select-list>

      <input-date-time
        data-testid="agenda-bloqueio-inicio-input"
        :label="t('Início')"
        :ng-required="true"
        :validation="() => isDataInicioValid"
        v-model="bloqueioData.bloqueio_agenda_data_hora_inicio"
        elem-id="bloqueio_agenda_data_hora_inicio">
      </input-date-time>

      <input-date-time
        data-testid="agenda-bloqueio-fim-input"
        :label="t('Fim')"
        :ng-required="true"
        :validation="() => isDataFimValid"
        v-model="bloqueioData.bloqueio_agenda_data_hora_fim"
        elem-id="bloqueio_agenda_data_hora_fim">
      </input-date-time>

    </box>
    </template>
  </modal>

  <!-- Delete Confirmation Modal -->
  <modal
    elem-id="modal_confirma_deletar_bloqueio"
    size="compactDesktop"
    :title="t('Deletar bloqueio da agenda')"
    save-label=""
    :close-label="t('Fechar')"
    :show="showDeleteModal"
    @close="hideDeleteConfirmation"
    @dismiss="hideDeleteConfirmation">

    <div class="confirmation-content">
      <h2>{{ t('Você tem certeza que quer fazer isso?') }}</h2>
      <Button
        data-testid="agenda-bloqueio-confirmar-deletar-btn"
        :label="t('Sim')"
        icon="pi pi-trash"
        severity="danger"
        @click="handleDelete"
      />
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useBloqueioAgenda } from '../../composables/useBloqueioAgenda';
import { permissionsService, permissionsState } from '../../services/permissionsService';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface OperadorOption {
  ID: string;
  Name: unknown;
}

interface BloqueioApiData {
  bloqueio_agenda_id?: number;
  bloqueio_agenda_titulo?: string;
  bloqueio_agenda_operador_user_id?: string;
  bloqueio_agenda_data_hora_inicio?: string;
  bloqueio_agenda_data_hora_fim?: string;
  [key: string]: unknown;
}

interface OpenEventPayload {
  bloqueioId?: number;
  id?: number;
  dataInicio?: string | null;
  [key: string]: unknown;
}

const { t } = useI18n();
const { selectedFranchises } = useFiltro();
const { fetchBloqueioAgenda, saveBloqueioAgenda, removerBloqueioAgenda } = useBloqueioAgenda();

// Reactive state
const isVisible = ref<boolean>(false);
const showDeleteModal = ref<boolean>(false);

const bloqueioData = reactive({
  bloqueio_agenda_id: 0,
  bloqueio_agenda_titulo: '',
  bloqueio_agenda_operador_user_id: '',
  bloqueio_agenda_data_hora_inicio: '',
  bloqueio_agenda_data_hora_fim: ''
});

const operadoresOptions = ref<OperadorOption[]>([]);

// Computed validations
const isTituloValid = computed((): boolean => !!bloqueioData.bloqueio_agenda_titulo);
const isOperadorValid = computed((): boolean => !!bloqueioData.bloqueio_agenda_operador_user_id);
const isDataInicioValid = computed((): boolean => !!bloqueioData.bloqueio_agenda_data_hora_inicio);

const isDataFimValid = computed((): boolean => {
  if (!bloqueioData.bloqueio_agenda_data_hora_fim) return false;
  if (isDataInicioValid.value) {
    const dataInicio = new Date(bloqueioData.bloqueio_agenda_data_hora_inicio);
    const dataFim = new Date(bloqueioData.bloqueio_agenda_data_hora_fim);
    return dataFim > dataInicio;
  }
  return true;
});

const validationErrors = computed((): string[] => {
  const errors: string[] = [];
  if (!isTituloValid.value) errors.push(t('Você deve informar o título'));
  if (!isOperadorValid.value) errors.push(t('Você deve informar o operador'));
  if (!isDataInicioValid.value) errors.push(t('Você deve informar uma data de início válida'));
  if (!isDataFimValid.value) errors.push(t('Você deve informar uma data de fim válida'));
  return errors;
});

const canSave = computed((): boolean =>
  isTituloValid.value && isOperadorValid.value && isDataInicioValid.value && isDataFimValid.value
);

// Methods
const loadOperadores = (): void => {
  log('AgendaBloqueioModal', 'loadOperadores', 'Building operators list from selected franchises');
  try {
    const operadoresMap: Record<string, OperadorOption> = {};
    const output: OperadorOption[] = [{ ID: '', Name: t('Escolha o operador') }];
    for (const franquia of selectedFranchises.value) {
      const franquiaId = (franquia as { id: number | string }).id;
      const operadoresFranquia = permissionsService.getOperadoresSelectArrayByFranquiaID(Number(franquiaId)) as OperadorOption[];
      for (const operador of operadoresFranquia) {
        if (operador.ID) {
          operadoresMap[operador.ID] = { ID: operador.ID, Name: operador.Name };
        }
      }
    }
    for (const id in operadoresMap) {
      output.push(operadoresMap[id]);
    }
    operadoresOptions.value = output;
    log('AgendaBloqueioModal', 'loadOperadores', 'Loaded operators', {
      franchises: selectedFranchises.value.length,
      operators: operadoresOptions.value.length - 1
    });
  } catch (error) {
    log('AgendaBloqueioModal', 'loadOperadores', 'Error loading operators', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar operadores'));
  }
};

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const resetForm = (): void => {
  log('AgendaBloqueioModal', 'resetForm', 'Resetting form');
  Object.assign(bloqueioData, {
    bloqueio_agenda_id: 0,
    bloqueio_agenda_titulo: '',
    bloqueio_agenda_operador_user_id: '',
    bloqueio_agenda_data_hora_inicio: '',
    bloqueio_agenda_data_hora_fim: ''
  });
};

const iniciaNovo = (dataInicio: string | null): void => {
  log('AgendaBloqueioModal', 'iniciaNovo', 'Initializing new bloqueio', dataInicio);
  resetForm();
  loadOperadores();
  if (dataInicio) {
    bloqueioData.bloqueio_agenda_data_hora_inicio = formatDateForInput(new Date(dataInicio));
    const dataFim = new Date(dataInicio);
    dataFim.setHours(dataFim.getHours() + 2);
    bloqueioData.bloqueio_agenda_data_hora_fim = formatDateForInput(dataFim);
  }
  isVisible.value = true;
};

const carregaExistente = async (bloqueioId: number): Promise<void> => {
  log('AgendaBloqueioModal', 'carregaExistente', 'Loading existing bloqueio', bloqueioId);
  try {
    loadOperadores();
    const data = await fetchBloqueioAgenda(bloqueioId) as BloqueioApiData;
    log('AgendaBloqueioModal', 'carregaExistente', 'Response', data);
    Object.assign(bloqueioData, {
      bloqueio_agenda_id: data.bloqueio_agenda_id || 0,
      bloqueio_agenda_titulo: data.bloqueio_agenda_titulo || '',
      bloqueio_agenda_operador_user_id: data.bloqueio_agenda_operador_user_id || '',
      bloqueio_agenda_data_hora_inicio: data.bloqueio_agenda_data_hora_inicio || '',
      bloqueio_agenda_data_hora_fim: data.bloqueio_agenda_data_hora_fim || ''
    });
    isVisible.value = true;
  } catch (error) {
    log('AgendaBloqueioModal', 'carregaExistente', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar bloqueio'));
  }
};

const handleSave = async (): Promise<void> => {
  log('AgendaBloqueioModal', 'handleSave', 'Saving bloqueio', bloqueioData);
  if (!canSave.value) {
    log('AgendaBloqueioModal', 'handleSave', 'Cannot save - validation errors', validationErrors.value);
    return;
  }
  try {
    const savedData = await saveBloqueioAgenda(bloqueioData) as unknown as BloqueioApiData;
    log('AgendaBloqueioModal', 'handleSave', 'Response', savedData);
    if (savedData?.bloqueio_agenda_id) {
      bloqueioData.bloqueio_agenda_id = savedData.bloqueio_agenda_id;
    }
    eventBus.emit('globalMessage.success', t('Bloqueio salvo com sucesso'));
    eventBus.emit('bloqueio.saved');
    isVisible.value = false;
    resetForm();
  } catch (error) {
    log('AgendaBloqueioModal', 'handleSave', 'Error', error);
    // The backend's named reason when it refused; the generic text when the request itself failed.
    const recusa = error as { kind?: string; message?: string } | null;
    const motivo = recusa?.kind === 'envelope-failure' && recusa.message ? recusa.message : 'Erro ao salvar bloqueio';
    eventBus.emit('globalMessage.error', t(motivo));
  }
};

const showDeleteConfirmation = (): void => {
  log('AgendaBloqueioModal', 'showDeleteConfirmation', 'Showing delete confirmation');
  showDeleteModal.value = true;
};

const hideDeleteConfirmation = (): void => {
  log('AgendaBloqueioModal', 'hideDeleteConfirmation', 'Hiding delete confirmation');
  showDeleteModal.value = false;
};

const handleDelete = async (): Promise<void> => {
  log('AgendaBloqueioModal', 'handleDelete', 'Deleting bloqueio', bloqueioData.bloqueio_agenda_id);
  try {
    await removerBloqueioAgenda(bloqueioData.bloqueio_agenda_id);
    log('AgendaBloqueioModal', 'handleDelete', 'Delete successful');
    eventBus.emit('globalMessage.success', t('Bloqueio deletado com sucesso'));
    eventBus.emit('bloqueio.deleted');
    showDeleteModal.value = false;
    isVisible.value = false;
    resetForm();
  } catch (error) {
    log('AgendaBloqueioModal', 'handleDelete', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao deletar bloqueio'));
  }
};

const handleClose = (): void => {
  log('AgendaBloqueioModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

const handleOpenEvent = (payload: OpenEventPayload): void => {
  log('AgendaBloqueioModal', 'handleOpenEvent', 'Received open event', payload);
  if (payload?.bloqueioId && payload.bloqueioId > 0) {
    void carregaExistente(payload.bloqueioId);
  } else {
    iniciaNovo(payload?.dataInicio || null);
  }
};

const handleModalOpen = (data: OpenEventPayload): void => {
  log('AgendaBloqueioModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data?.id && data.id > 0) {
    void carregaExistente(data.id);
  } else if (data?.bloqueioId && data.bloqueioId > 0) {
    void carregaExistente(data.bloqueioId);
  } else {
    iniciaNovo(data?.dataInicio || null);
  }
};

// Lifecycle
onMounted(() => {
  log('AgendaBloqueioModal', 'mounted', 'Component mounted');
  eventBus.on('modal.agenda-bloqueio.open', handleModalOpen as (data?: unknown) => void);
  eventBus.on('modal.agenda-bloqueio.close', handleClose);
  eventBus.on('bloqueio-agenda.open', handleOpenEvent as (data?: unknown) => void);
  if (!permissionsState.permissionsLoaded) {
    permissionsService.initialize().catch((error) => {
      log('AgendaBloqueioModal', 'mounted', 'Error initializing permissions', error);
    });
  }
});

onBeforeUnmount(() => {
  log('AgendaBloqueioModal', 'beforeUnmount', 'Component unmounting');
  eventBus.off('modal.agenda-bloqueio.open', handleModalOpen as (data?: unknown) => void);
  eventBus.off('modal.agenda-bloqueio.close', handleClose);
  eventBus.off('bloqueio-agenda.open', handleOpenEvent as (data?: unknown) => void);
});

defineExpose({
  open: handleOpenEvent,
  close: handleClose,
  iniciaNovo,
  carregaExistente
});
</script>

<style scoped>
.delete-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.confirmation-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px;
  text-align: center;
}

.confirmation-content h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
  font-weight: 600;
}
</style>
